/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Card, Col, Row, Spin, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import GlobalFooter from '../components/Layout/GlobalFooter';
import DocumentTitle from '../components/DocumentTitle';

// Misc imports
import { NAV_LOGIN } from '../constants/navigation';
import AppLogo from '../assets/logo-full.svg';
import { useVerifyEmailMutation } from '../store/auth/authApi';

// Constant declarations
const baseClassName = 'email-validation-screen';
const { Text } = Typography;

const EmailVerificationScreen = () => {
  const { token: paramsToken } = useParams();
  const token = new URLSearchParams(window.location.search).get('token') || paramsToken;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [verifyEmail, { isLoading, isSuccess: isVerified, reset: resetVerify }] = useVerifyEmailMutation();

  // On mount
  useEffect(() => {
    if (token) {
      verifyEmail({ token });
    }

    return () => {
      resetVerify();
    };
  }, [token]);

  const renderEmailVerificationContent = () => {
    if (isLoading) {
      return (
        <div className="text-center">
          <div>
            <Spin size="large" indicator={<LoadingOutlined className="fs-xxl" spin />} />
          </div>

          <div className="mt-5">
            <h3>{t('Is verifying')}...</h3>
          </div>
        </div>
      );
    } else if (isVerified) {
      return (
        <div className="text-center">
          <Text
            className="fs-xxxxl mb-0"
            type="success"
          >
            <CheckCircleOutlined />
          </Text>

          <h2>{t('Your email has been verified successfully! You can now log in.')}</h2>

          <Button
            type="primary"
            size="large"
            onClick={() => navigate(NAV_LOGIN)}
          >
            {t('Log in')}
          </Button>
        </div>
      );
    } else {
      return (
        <div className="text-center">
          <Text
            className="fs-xxxxl mb-0"
            type="danger"
          >
            <CloseCircleOutlined />
          </Text>

          <h2>{t('Your email could not be verified')}.</h2>
        </div>
      );
    }
  };

  return (
    <div className={baseClassName}>
      <DocumentTitle title="Verify your email" />

      <Row justify="center">
        <img src={AppLogo} alt="logo" width={200} className="pt-10 mt-10 mb-10" />
      </Row>

      <Row justify="center">
        <Col>
          <Card
            title={(
              <Row justify="center">
                <Col>
                  {t('Email Verification')}
                </Col>
              </Row>
            )}
            bordered={false}
          >
            {renderEmailVerificationContent()}
          </Card>
        </Col>
      </Row>

      <Row justify="center">
        <Col>
          <GlobalFooter />
        </Col>
      </Row>
    </div>
  );
};

export default EmailVerificationScreen;
